import {
  Button,
  CustomRewardBalance,
  CustomRewardCurrency,
  SegmentedControl,
  Typography,
} from "@joonapp/web-shared"
import { useState } from "react"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { useRedemptionSuccessModalStore } from "./RedemptionSuccessModal"
import Counter from "../../../components/counter/Counter"
import SlideUpModal from "../../../components/slideUpModal/SlideUpModal"
import { QUERY_KEYS } from "../../../constants"
import { useFamilyQuery } from "../../../networking/queries"
import { useRedeemRewardMutation } from "../../../networking/rewards/mutations"
import { FamilyQuerySelect } from "../../../types"
import { trackAnalyticEvent } from "../../../util/analytics"
import { ANALYTIC_EVENTS } from "../../../util/analytics"
import { queryClient } from "../../../util/queryClient"
import { localStorageItems } from "../../../util/storage"
import { getScreenTimeConfigurations } from "../../../util/util"

interface RewardQuantityModalStore {
  isOpen: boolean
  onOpen: (rewardBalance: CustomRewardBalance) => void
  onClose: () => void
  rewardBalance: CustomRewardBalance | null
  quantity: number
  setQuantity: (quantity: number) => void
}

enum ScreenTimeUsageType {
  LINKED_DEVICE,
  OTHER_DEVICE,
}

const initialState = {
  isOpen: false,
  rewardBalance: null,
  quantity: 1,
}

export const useRewardQuantityModal = create<RewardQuantityModalStore>(
  (set) => ({
    ...initialState,
    onOpen: (rewardBalance: CustomRewardBalance) =>
      set({ isOpen: true, rewardBalance }),
    onClose: () => set(initialState),
    setQuantity: (quantity: number) => set({ quantity }),
  })
)

export default function RewardQuantityModal() {
  const { isOpen, onClose, rewardBalance, quantity, setQuantity } =
    useRewardQuantityModal()
  const { onOpen: onOpenRedemptionSuccessModal } =
    useRedemptionSuccessModalStore()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const [screenTimeUsageType, setScreenTimeUsageType] =
    useState<ScreenTimeUsageType>(ScreenTimeUsageType.LINKED_DEVICE)
  const { t } = useTranslation()
  const selectedChild = children?.find(
    (child) => child.user.id === rewardBalance?.user_id
  )
  const { numLinkedDevices } = getScreenTimeConfigurations(
    rewardBalance?.reward || null,
    rewardBalance?.user_id
  )

  const redeemRewardMutation = useRedeemRewardMutation()

  const isMinuteReward =
    rewardBalance?.reward?.currency === CustomRewardCurrency.MINUTE
  const isDollarReward =
    rewardBalance?.reward?.currency === CustomRewardCurrency.DOLLAR

  const onRedeemClick = async () => {
    if (!rewardBalance) return
    trackAnalyticEvent(ANALYTIC_EVENTS.REDEEM_CUSTOM_REWARD)
    await redeemRewardMutation.mutateAsync({
      rewardId: rewardBalance.reward.id,
      amount: quantity,
      userId: rewardBalance.user_id,
    })
    const isOne = quantity === 1
    const redeemKey = isDollarReward
      ? isOne
        ? "RQM_TOAST_DOLLAR_ONE"
        : "RQM_TOAST_DOLLAR_OTHER"
      : isMinuteReward
        ? isOne
          ? "RQM_TOAST_MINUTE_ONE"
          : "RQM_TOAST_MINUTE_OTHER"
        : isOne
          ? "RQM_TOAST_REWARD_ONE"
          : "RQM_TOAST_REWARD_OTHER"
    toast.success(t(redeemKey, { count: quantity }))
    onOpenRedemptionSuccessModal({
      rewardBalance,
      childName: selectedChild?.user.name || undefined,
      quantity,
    })
    onClose()
  }

  const onDeductBalanceClick = async () => {
    if (!rewardBalance) return
    trackAnalyticEvent(ANALYTIC_EVENTS.DEDUCT_CUSTOM_REWARD_BALANCE)
    await redeemRewardMutation.mutateAsync({
      rewardId: rewardBalance.reward.id,
      amount: quantity,
      userId: rewardBalance.user_id,
      extra_action: "auto_deduct",
    })
    toast.success(
      isMinuteReward
        ? t("RQM_TOAST_DEDUCT_MINUTE", { count: quantity })
        : t("RQM_TOAST_DEDUCT_DOLLAR", { count: quantity })
    )
    onClose()
  }

  const onStartTimerClick = async () => {
    if (!rewardBalance) return
    trackAnalyticEvent(ANALYTIC_EVENTS.REDEEM_CUSTOM_REWARD)
    await redeemRewardMutation.mutateAsync({
      rewardId: rewardBalance.reward.id,
      amount: quantity,
      userId: rewardBalance.user_id,
      extra_action: "start_active_session",
    })
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.SCREEN_TIME_ACTIVE_SESSION],
    })
    toast.success(t("RQM_TOAST_TIMER_STARTED"))
    onClose()
  }

  if (!rewardBalance) return null

  const needsToFinishSetup =
    rewardBalance.reward.currency === CustomRewardCurrency.MINUTE &&
    numLinkedDevices === 0 &&
    !localStorage.getItem(
      localStorageItems.screentimeReminderDismissed +
        rewardBalance.reward.id.toString()
    )

  const displayName = selectedChild?.nickname || selectedChild?.user.name

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onClose}
      zIndex={1000}
      backgroundType="overlay"
      size="fit-content"
    >
      <div className="w-full h-full flex flex-col align-start gap-6 p-4">
        <div className="flex flex-col align-start justify-start gap-4">
          <Typography variant="bodyBold" style={{ wordBreak: "break-word" }}>
            {isMinuteReward
              ? t("RQM_APPROVING", {
                  name: `${rewardBalance.reward.emoji} ${rewardBalance.reward.title}`,
                })
              : t("RQM_USING", {
                  name: `${rewardBalance.reward.emoji} ${rewardBalance.reward.title}`,
                })}
          </Typography>
          <Typography variant="bodySmall">
            {isMinuteReward
              ? t("RQM_SET_AMOUNT_MINUTES")
              : t("RQM_SET_AMOUNT_DOLLARS")}
          </Typography>
          <Counter
            maxValue={rewardBalance.balance}
            value={quantity}
            setValue={setQuantity}
          />
          {!needsToFinishSetup && isMinuteReward && (
            <div className="w-full flex flex-col gap-2">
              <Typography variant="bodySmall">
                {t("RQM_HOW_WILL_USE", { name: displayName })}
              </Typography>
              <SegmentedControl
                options={[
                  {
                    label: t("RQM_ON_LINKED"),
                    value: ScreenTimeUsageType.LINKED_DEVICE,
                  },
                  {
                    label: t("RQM_ON_OTHER"),
                    value: ScreenTimeUsageType.OTHER_DEVICE,
                  },
                ]}
                value={screenTimeUsageType}
                setValue={(value) =>
                  setScreenTimeUsageType(value as ScreenTimeUsageType)
                }
                fullWidth
              />
            </div>
          )}
          {isMinuteReward ? (
            <>
              {screenTimeUsageType === ScreenTimeUsageType.LINKED_DEVICE &&
              !needsToFinishSetup ? (
                <div className="flex flex-col gap-2">
                  <Typography variant="bodyXSmall">
                    {t("RQM_ACTIVATE_INFO")}
                  </Typography>
                  <img
                    src="/images/screenTimeControlsSteps/st_use_linked_device_2x.png"
                    alt="Screen time usage on linked devices"
                    className="w-full mb-2 rounded-sm"
                  />
                  <Button
                    buttonType="primary"
                    onClick={onRedeemClick}
                    text={
                      isMinuteReward ? t("REWARD_APPROVE") : t("REWARD_USE")
                    }
                    fullWidth
                    disabled={quantity < 1 || quantity > rewardBalance.balance}
                    isLoading={redeemRewardMutation.isLoading}
                    style={{ userSelect: "none" }}
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <Typography variant="bodySmall">
                    {t("RQM_TRACK_OTHER")}
                  </Typography>
                  <Button
                    text={t("RQM_START_TIMER", { count: quantity })}
                    onClick={onStartTimerClick}
                    fullWidth
                    disabled={quantity < 1 || quantity > rewardBalance.balance}
                  />
                  <Typography variant="bodySmall" className="mt-1">
                    {t("RQM_ALREADY_SPENT")}
                  </Typography>
                  <Button
                    buttonType="secondary"
                    text={t("RQM_DEDUCT_BALANCE", { count: quantity })}
                    onClick={onDeductBalanceClick}
                    fullWidth
                  />
                </div>
              )}
            </>
          ) : (
            <Button
              buttonType="primary"
              onClick={onRedeemClick}
              text={isMinuteReward ? "Approve" : "Use"}
              fullWidth
              disabled={quantity < 1 || quantity > rewardBalance.balance}
              isLoading={redeemRewardMutation.isLoading}
              style={{ userSelect: "none" }}
            />
          )}
        </div>
      </div>
    </SlideUpModal>
  )
}
